<template>
    <div class="searchable-select" :class="{'ss-disabled': disabled}" v-click-outside-ss="close">
        <input
            ref="input"
            type="text"
            class="inborder ss-input"
            :class="inputClass"
            :style="inputStyle"
            :disabled="disabled"
            :placeholder="placeholder"
            v-model="term"
            autocomplete="off"
            @focus="open"
            @input="onType"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="pickHighlighted"
            @keydown.esc.prevent="close"
        />
        <span class="ss-caret" @click="toggle"><i class="fas fa-arrow-down"></i></span>

        <div class="ss-menu" :class="{'ss-menu-up': dropUp}" v-if="opened">
            <div class="ss-status" v-if="loading">{{ loadingText }}</div>
            <div class="ss-status" v-else-if="!items.length">{{ emptyText }}</div>
            <div
                v-else
                v-for="(item, index) in items"
                :key="item.value"
                class="ss-option"
                :class="{'ss-active': index === highlighted, 'ss-selected': item.value === value}"
                @mousedown.prevent="pick(item)"
                @mouseenter="highlighted = index"
            >{{ item.text }}</div>

            <div class="ss-status ss-hint" v-if="!loading && hint">{{ hint }}</div>
        </div>
    </div>
</template>

<script>
/**
 * A select that searches on the server instead of holding every row in the DOM.
 *
 * The parent owns the data: this component emits `search` (debounced) and
 * renders whatever comes back in `items`. That keeps a 4000-row category list
 * off the page - the user sees the first handful and narrows from there.
 */
export default {
    name: 'SearchableSelect',
    directives: {
        // Vue 2 has no built-in click-outside, and the dropdown has to close
        // when the user clicks elsewhere in the sidebar.
        'click-outside-ss': {
            bind(el, binding) {
                el.__ssOutside = (event) => {
                    if (!el.contains(event.target)) binding.value(event);
                };
                document.addEventListener('mousedown', el.__ssOutside);
            },
            unbind(el) {
                document.removeEventListener('mousedown', el.__ssOutside);
                delete el.__ssOutside;
            },
        },
    },
    props: {
        value: { default: null },
        items: { type: Array, default: () => [] },
        // Label for the current value; needed because the selected row may not
        // be in `items` (it is rarely inside the first page of results).
        selectedText: { type: String, default: '' },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        loadingText: { type: String, default: '...' },
        emptyText: { type: String, default: '-' },
        hint: { type: String, default: '' },
        debounce: { type: Number, default: 300 },
        inputClass: { type: String, default: '' },
        inputStyle: { type: String, default: '' },
    },
    data() {
        return {
            opened: false,
            term: this.selectedText,
            highlighted: -1,
            timer: null,
            dirty: false,
            dropUp: false,
        }
    },
    watch: {
        selectedText(next) {
            // Only follow the parent while the user is not mid-search, so the
            // box does not snap back under them as they type.
            if (!this.opened || !this.dirty) {
                this.term = next;
            }
        },
        items() {
            this.highlighted = this.items.length ? 0 : -1;
        },
    },
    methods: {
        /**
         * These selects sit inside scrolling sidebars, so a menu that always
         * dropped downwards would be clipped for fields near the bottom.
         */
        updateDirection() {
            const el = this.$refs.input;
            if (!el || typeof el.getBoundingClientRect !== 'function') return;
            const rect = el.getBoundingClientRect();
            const below = window.innerHeight - rect.bottom;
            this.dropUp = below < 250 && rect.top > below;
        },
        open() {
            if (this.disabled) return;
            this.updateDirection();
            this.opened = true;
            this.dirty = false;
            // Opening with the selected label still in the box would filter the
            // list down to that one row, so ask for the default page instead.
            this.term = '';
            this.$emit('search', '');
        },
        close() {
            if (!this.opened) return;
            this.opened = false;
            this.dirty = false;
            this.term = this.selectedText;
        },
        toggle() {
            if (this.opened) {
                this.close();
            } else {
                this.$refs.input && this.$refs.input.focus();
            }
        },
        onType() {
            this.dirty = true;
            this.opened = true;
            if (this.timer) clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.$emit('search', this.term);
            }, this.debounce);
        },
        move(step) {
            if (!this.opened) {
                this.open();
                return;
            }
            if (!this.items.length) return;
            const next = this.highlighted + step;
            this.highlighted = Math.max(0, Math.min(this.items.length - 1, next));
        },
        pickHighlighted() {
            if (this.highlighted >= 0 && this.items[this.highlighted]) {
                this.pick(this.items[this.highlighted]);
            }
        },
        pick(item) {
            this.$emit('input', item.value);
            this.$emit('picked', item);
            this.opened = false;
            this.dirty = false;
            this.term = item.text;
        },
    },
    beforeDestroy() {
        if (this.timer) clearTimeout(this.timer);
    },
}
</script>

<style scoped>
.searchable-select {
    position: relative;
    width: 100%;
}
.ss-input {
    width: 100%;
    padding-inline-end: 24px;
}
.ss-disabled .ss-input {
    background: #eee;
}
.ss-caret {
    position: absolute;
    inset-inline-end: 6px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #555;
    cursor: pointer;
    pointer-events: auto;
}
.ss-menu {
    position: absolute;
    top: calc(100% + 2px);
    inset-inline-start: 0;
    width: 100%;
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #bbb;
    border-radius: 4px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    /* Stays above the b-sidebar body without escaping its stacking context. */
    z-index: 2000;
}
.ss-menu.ss-menu-up {
    top: auto;
    bottom: calc(100% + 2px);
}
.ss-option {
    padding: 5px 8px;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.ss-option.ss-active {
    background: #e8f0fe;
}
.ss-option.ss-selected {
    font-weight: 700;
}
.ss-status {
    padding: 6px 8px;
    font-size: 12px;
    color: #777;
}
.ss-hint {
    border-top: 1px solid #eee;
}
</style>
